import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Gift, Sparkles, MessageCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

type JourneyType = 'menstrual' | 'pregnancy' | 'postpartum' | 'ivf' | 'perimenopause' | 'menopause';

interface TherapyTokenGiftProps {
  journeyType: JourneyType;
}

const JOURNEY_MESSAGES: Record<JourneyType, { title: string; message: string }> = {
  menstrual: {
    title: "Supporting Your Cycle Journey",
    message: "We know tracking your cycle can bring up questions, concerns, and moments of uncertainty. You deserve compassionate support as you navigate the ebbs and flows of your body's rhythms."
  },
  pregnancy: {
    title: "Supporting Your Pregnancy Journey",
    message: "Pregnancy is a profound transformation filled with joy, wonder, and sometimes anxiety. You deserve compassionate guidance through every trimester, every concern, and every beautiful moment of growing new life."
  },
  postpartum: {
    title: "Supporting Your Postpartum Journey",
    message: "The postpartum period can be one of the most challenging yet precious times. Whether you're navigating sleep deprivation, healing, or adjusting to life with your baby, you deserve unwavering support and understanding."
  },
  ivf: {
    title: "Supporting Your Fertility Journey",
    message: "IVF and fertility treatments can be emotionally and physically demanding. The uncertainty, the hope, the disappointments - you don't have to carry this alone. You deserve compassionate support through every step."
  },
  perimenopause: {
    title: "Supporting Your Perimenopause Journey",
    message: "Perimenopause can feel like an unpredictable transition with physical and emotional changes. You deserve support as you navigate hot flashes, mood shifts, and this powerful phase of transformation."
  },
  menopause: {
    title: "Supporting Your Menopause Journey",
    message: "Menopause marks a significant life transition that deserves recognition and support. Through the changes, the wisdom gained, and the new chapter ahead - you deserve compassionate guidance."
  }
};

export default function TherapyTokenGift({ journeyType }: TherapyTokenGiftProps) {
  const [open, setOpen] = useState(false);
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const navigate = useNavigate();
  const journeyMessage = JOURNEY_MESSAGES[journeyType];

  useEffect(() => {
    checkEligibility();
  }, []);

  const checkEligibility = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_credits')
        .select('free_therapy_tokens_claimed')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      // Track if already claimed
      setAlreadyClaimed(data?.free_therapy_tokens_claimed || false);
    } catch (error) {
      console.error('Error checking eligibility:', error);
    }
  };

  const claimTokens = async () => {
    if (alreadyClaimed) {
      toast.error("You've already redeemed your free tokens", {
        description: "Ready for more AI support? Check out our Therappi AI subscription packages.",
        duration: 6000,
        action: {
          label: "View Packages",
          onClick: () => navigate('/subscription')
        }
      });
      return;
    }

    try {
      setClaiming(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please log in to claim your gift');
        return;
      }

      // Use the manage-credits edge function to add 10 tokens
      const { data, error } = await supabase.functions.invoke('manage-credits', {
        body: {
          action: 'add',
          amount: 10,
          source: 'journey_gift'
        }
      });

      if (error) throw error;

      // Mark as claimed
      const { error: updateError } = await supabase
        .from('user_credits')
        .update({
          free_therapy_tokens_claimed: true,
          free_therapy_tokens_claimed_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      toast.success('10 Therappi AI Tokens Added! 🎁', {
        description: 'Your gift tokens are ready to use. Navigate to Therappi to start your conversation.',
        duration: 6000
      });

      setAlreadyClaimed(true);
      setOpen(false);
    } catch (error: any) {
      console.error('Error claiming tokens:', error);
      toast.error('Failed to claim tokens. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="gap-2 border-accent/30 bg-gradient-to-r from-accent/5 to-primary/5 hover:from-accent/10 hover:to-primary/10"
        >
          <Gift className="h-4 w-4 text-accent" />
          {alreadyClaimed ? "Therappi® AI Support" : "Claim Free Therappi® Tokens"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="sr-only">Therappi® AI Gift</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center text-center space-y-4 py-4">
          <div className="relative">
            <div className="absolute inset-0 bg-accent/20 rounded-full blur-xl animate-pulse" />
            <Gift className="h-16 w-16 text-accent relative z-10" />
            <Sparkles className="h-6 w-6 text-primary absolute -top-2 -right-2 animate-bounce" />
          </div>
          
          <div className="space-y-3">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
              {alreadyClaimed ? "Need More Support?" : journeyMessage.title}
            </h3>
            
            {!alreadyClaimed && (
              <>
                <p className="text-muted-foreground text-base leading-relaxed">
                  {journeyMessage.message}
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20">
                  <MessageCircle className="h-5 w-5 text-accent" />
                  <span className="text-xl font-bold text-accent">10 Free Therappi AI Tokens</span>
                </div>
              </>
            )}
            
            {alreadyClaimed && (
              <p className="text-muted-foreground text-base">
                You've already received your free tokens, but Therappi AI is here whenever you need support.
              </p>
            )}
          </div>

          <p className="text-sm text-muted-foreground max-w-md">
            {alreadyClaimed 
              ? "Continue your journey with unlimited AI conversations through our subscription packages."
              : "Use these tokens to chat with Therappi AI - your compassionate AI companion for support, guidance, and understanding during this special time."}
          </p>

          <div className="flex gap-3 pt-2">
            <Button 
              onClick={alreadyClaimed ? () => navigate('/subscription') : claimTokens}
              disabled={claiming}
              className="bg-gradient-to-r from-accent to-primary hover:opacity-90 transition-opacity"
              size="lg"
            >
              {claiming ? (
                <>
                  <Sparkles className="h-4 w-4 mr-2 animate-spin" />
                  Claiming...
                </>
              ) : alreadyClaimed ? (
                <>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  View Packages
                </>
              ) : (
                <>
                  <Gift className="h-4 w-4 mr-2" />
                  Claim Your Gift
                </>
              )}
            </Button>
          </div>

          {!alreadyClaimed && (
            <p className="text-xs text-muted-foreground italic">
              This is a one-time gift - once claimed, you can use these tokens anytime!
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}